import { useCallback, useState } from 'react';

interface UseItemEditorOptions<T> {
    makeEmpty: () => T;
    save: (item: T) => Promise<unknown>;
}

interface ItemEditor<T> {
    draft: T;
    setDraft: React.Dispatch<React.SetStateAction<T>>;
    isOpen: boolean;
    isEditing: boolean;
    openAdd: () => void;
    openEdit: (item: T) => void;
    close: () => void;
    commit: () => Promise<void>;
}

// Add/Edit-in-a-popup state for the list steps
export function useItemEditor<T>({ makeEmpty, save }: UseItemEditorOptions<T>): ItemEditor<T> {
    const [draft, setDraft] = useState<T>(makeEmpty);
    const [isOpen, setIsOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);

    const openAdd = useCallback(() => {
        setDraft(makeEmpty());
        setIsEditing(false);
        setIsOpen(true);
    }, [makeEmpty]);

    const openEdit = useCallback((item: T) => {
        setDraft(item);
        setIsEditing(true);
        setIsOpen(true);
    }, []);

    const close = useCallback(() => setIsOpen(false), []);

    const commit = useCallback(async () => {
        await save(draft);
    }, [draft, save]);

    return { draft, setDraft, isOpen, isEditing, openAdd, openEdit, close, commit };
}
